import { ReactNode } from 'react';
import { Copy, Check, Trash2 } from 'lucide-react';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';

interface ToolLayoutProps {
  title: string;
  description: string;
  inputLabel?: string;
  inputPlaceholder?: string;
  inputValue: string;
  onInputChange: (value: string) => void;
  outputLabel?: string;
  output: ReactNode;
  onClear?: () => void;
  actions?: ReactNode;
  isLoading?: boolean;
  privacyNote?: boolean;
}

export function ToolLayout({
  title: _title,
  description: _description,
  inputLabel = 'Input',
  inputPlaceholder = 'Enter your input here...',
  inputValue,
  onInputChange,
  outputLabel = 'Output',
  output,
  onClear,
  actions,
  isLoading = false,
  privacyNote = true,
}: ToolLayoutProps) {
  const { copied, copy } = useCopyToClipboard();

  const handleCopy = () => {
    if (typeof output === 'string') {
      copy(output);
    } else {
      // Try to get text content from the output element
      const outputElement = document.getElementById('tool-output');
      if (outputElement) {
        copy(outputElement.textContent || '');
      }
    }
  };

  const handleClear = () => {
    onInputChange('');
    onClear?.();
  };

  return (
    <div className="flex flex-col gap-4 sm:gap-6 h-full flex-1">
      {/* Privacy Note */}
      {privacyNote && (
        <div className="flex items-start gap-2 text-xs text-[var(--accent)] bg-[var(--accent-dim)] px-3 py-2 rounded-lg">
          <svg
            className="w-4 h-4 shrink-0 mt-0.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
            />
          </svg>
          <span className="leading-relaxed">
            All decoding happens locally in your browser. No sensitive data is sent to our servers.
          </span>
        </div>
      )}

      {/* Input Section */}
      <div className="space-y-2">
        <label className="block text-xs sm:text-sm font-medium text-[var(--text-primary)]">
          {inputLabel}
        </label>
        <textarea
          className="terminal-input min-h-[120px] sm:min-h-[150px] resize-y"
          placeholder={inputPlaceholder}
          value={inputValue}
          onChange={(e) => onInputChange(e.target.value)}
          spellCheck={false}
        />
      </div>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
        {actions}
        {inputValue && (
          <button
            type="button"
            onClick={handleClear}
            className="btn-secondary flex items-center gap-2"
          >
            <Trash2 className="w-4 h-4" />
            <span className="hidden sm:inline">Clear</span>
          </button>
        )}
      </div>

      {/* Output Section */}
      <div className="flex flex-col gap-2 flex-1 min-h-0">
        <div className="flex items-center justify-between">
          <label className="block text-xs sm:text-sm font-medium text-[var(--text-primary)]">
            {outputLabel}
          </label>
          {output && (
            <button
              type="button"
              onClick={handleCopy}
              className="flex items-center gap-1 text-xs text-[var(--text-muted)] hover:text-[var(--accent)] transition-colors"
            >
              {copied ? (
                <>
                  <Check className="w-3 h-3" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="w-3 h-3" />
                  Copy
                </>
              )}
            </button>
          )}
        </div>
        <div
          id="tool-output"
          className={`output-panel flex-1 ${isLoading ? 'animate-pulse' : ''}`}
        >
          {isLoading ? (
            <div className="flex items-center gap-2 text-[var(--text-muted)]">
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Processing...
            </div>
          ) : output ? (
            output
          ) : (
            <span className="text-[var(--text-muted)]">
              Output will appear here...
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
